import { Toolbar } from '../../ui/Toolbar';
import { useTextmenuCommands } from './hooks/useTextmenuCommands';
import { useTextmenuStates } from './hooks/useTextmenuStates';
import { Editor } from '@tiptap/react';
import { memo, useCallback, useEffect, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Surface } from '../../ui/Surface';
import { ColorPicker } from '../../panels';
import { FontFamilyPicker } from './components/FontFamilyPicker';
import { FontSizePicker } from './components/FontSizePicker';
import { useTextmenuContentTypes } from './hooks/useTextmenuContentTypes';
import { ContentTypePicker } from './components/ContentTypePicker';
// import { AIDropdown } from './components/AIDropdown';
import { EditLinkPopover } from './components/EditLinkPopover';
import { Popper } from '@mui/material';
import { usePopover } from 'hooks/common/usePopover';
import BoldIcon from '../../SVG/BoldIcon';
import ItalicIcon from '../../SVG/ItalicIcon';
import UnderlineIcon from '../../SVG/UnderlineIcon';
import StrikethroughIcon from '../../SVG/StrikethroughIcon';
import CodeIcon from '../../SVG/CodeIcon';
import FileCodeIcon from '../../SVG/FileCodeIcon';
import HighlighterIcon from '../../SVG/HighlighterIcon';
import PaletteIcon from '../../SVG/PaletteIcon';
import SubsCriptx from '../../SVG/SubsCriptx';
import SuperScriptx from '../../SVG/SuperScriptx';
import AlignLeft from '../../SVG/AlignLeft';
import AlignCenter from '../../SVG/AlignCenter';
import AlignRight from '../../SVG/AlignRight';
import AlignJustify from '../../SVG/AlignJustify';
import { DotsHorizontal } from 'components/SVG';
import { ListOptionsType } from 'types/common/DropdownOption';

// We memorize the button so each button is not rerendered
// on every editor state change
const MemoButton = memo(Toolbar.Button);
const MemoColorPicker = memo(ColorPicker);
const MemoFontFamilyPicker = memo(FontFamilyPicker);
const MemoFontSizePicker = memo(FontSizePicker);
const MemoContentTypePicker = memo(ContentTypePicker);

export type TextMenuProps = {
  editor: Editor;
  appendTo?: React.RefObject<any>;
  setNodeSpecificOptions:
    | React.Dispatch<React.SetStateAction<ListOptionsType[]>>
    | undefined;
};

export const TextMenu = ({
  editor,
  appendTo,
  setNodeSpecificOptions,
}: TextMenuProps) => {
  const [selecting, setSelecting] = useState(false);
  const commands = useTextmenuCommands(editor);
  const states = useTextmenuStates(editor);
  const blockOptions = useTextmenuContentTypes(editor);

  // Function to update the menu position based on text selection
  // const updateMenuPosition = useCallback(() => {
  //   if (!editor.isActive || !editor.view) {
  //     closePopover();
  //     setIsTextMenuPopoverOpen && setIsTextMenuPopoverOpen(false);
  //     return;
  //   }

  //   // Check if there's valid text selection
  //   if (
  //     !states.shouldShow({
  //       view: editor.view,
  //       from: editor.state.selection.from,
  //     })
  //   ) {
  //     closePopover();
  //     setIsTextMenuPopoverOpen && setIsTextMenuPopoverOpen(false);
  //     return;
  //   }

  //   // Get DOM selection
  //   const domSelection = window.getSelection();
  //   if (!domSelection || domSelection.rangeCount === 0) {
  //     closePopover();
  //     setIsTextMenuPopoverOpen && setIsTextMenuPopoverOpen(false);
  //     return;
  //   }
  //   if (domSelection.anchorNode?.parentElement) {
  //     openPopover(domSelection.anchorNode?.parentElement as HTMLElement);
  //     setIsTextMenuPopoverOpen && setIsTextMenuPopoverOpen(true);
  //     return;
  //   }

  //   // Use the appendTo container or document.body as the anchor
  //   if (appendTo?.current) {
  //     openPopover(appendTo.current);
  //     setIsTextMenuPopoverOpen && setIsTextMenuPopoverOpen(true);
  //   } else {
  //     // Use document.body if no appendTo is provided
  //     openPopover(document.body);
  //     setIsTextMenuPopoverOpen && setIsTextMenuPopoverOpen(true);
  //   }
  // }, [editor, states, openPopover, closePopover, appendTo]);

  // useEffect(() => {
  //   let selectionTimeout: number;

  //   const onSelectionChange = () => {
  //     console.log('onSelectionChange');
  //     setSelecting(true);
  //     if (selectionTimeout) {
  //       window.clearTimeout(selectionTimeout);
  //     }

  //     selectionTimeout = window.setTimeout(() => {
  //       setSelecting(false);
  //       updateMenuPosition();
  //     }, 200);
  //   };

  //   editor.on('selectionUpdate', onSelectionChange);

  //   return () => {
  //     editor.off('selectionUpdate', onSelectionChange);
  //   };
  // }, [editor, updateMenuPosition]);
  // console.log('anchorEl', anchorEl);

  useEffect(() => {
    if (!editor) return;
    const handleSelectionUpdate = () => {
      setNodeSpecificOptions?.([
        {
          label: 'Type Picker',
          tag: 'typePicker',
          onClick: () => {},
          icon: <MemoContentTypePicker options={blockOptions} />,
        },
        {
          label: 'Font Size Picker',
          tag: 'fontSizePicker',
          onClick: () => {},
          icon: (
            <>
              <MemoFontSizePicker
                onChange={commands.onSetFontSize}
                value={states.currentSize || ''}
              />
              <Toolbar.Divider />
            </>
          ),
        },
        {
          label: 'Bold',
          tag: 'bold',
          onClick: () => {},
          icon: (
            <MemoButton
              tooltip='Bold'
              tooltipShortcut={['Mod', 'B']}
              onClick={commands.onBold}
              active={states.isBold}
            >
              <BoldIcon />
            </MemoButton>
          ),
        },
        {
          label: 'Italic',
          tag: 'italic',
          onClick: () => {},
          icon: (
            <MemoButton
              tooltip='Italic'
              tooltipShortcut={['Mod', 'I']}
              onClick={commands.onItalic}
              active={states.isItalic}
            >
              <ItalicIcon />
            </MemoButton>
          ),
        },
        {
          label: 'Underline',
          tag: 'underline',
          onClick: () => {},
          icon: (
            <MemoButton
              tooltip='Underline'
              tooltipShortcut={['Mod', 'U']}
              onClick={commands.onUnderline}
              active={states.isUnderline}
            >
              <UnderlineIcon />
            </MemoButton>
          ),
        },
        {
          label: 'Strikethrough',
          tag: 'strikethrough',
          onClick: () => {},
          icon: (
            <MemoButton
              tooltip='Strikethrough'
              tooltipShortcut={['Mod', 'Shift', 'S']}
              onClick={commands.onStrike}
              active={states.isStrike}
            >
              <StrikethroughIcon />
            </MemoButton>
          ),
        },
        {
          label: 'Code',
          tag: 'code',
          onClick: () => {},
          icon: (
            <MemoButton
              tooltip='Code'
              tooltipShortcut={['Mod', 'E']}
              onClick={commands.onCode}
              active={states.isCode}
            >
              <CodeIcon />
            </MemoButton>
          ),
        },
        {
          label: 'Code block',
          tag: 'codeBlock',
          onClick: () => {},
          icon: (
            <MemoButton tooltip='Code block' onClick={commands.onCodeBlock}>
              <FileCodeIcon />
            </MemoButton>
          ),
        },
        {
          label: 'Edit Link',
          tag: 'editLink',
          onClick: () => {},
          icon: <EditLinkPopover onSetLink={commands.onLink} />,
        },
        {
          label: 'Highlight text',
          tag: 'highlight',
          onClick: () => {},
          icon: (
            <Popover.Root>
              <Popover.Trigger asChild>
                <MemoButton
                  active={!!states.currentHighlight}
                  tooltip='Highlight text'
                >
                  <HighlighterIcon />
                </MemoButton>
              </Popover.Trigger>
              <Popover.Content side='top' sideOffset={8} asChild>
                <Surface className='p-1'>
                  <MemoColorPicker
                    color={states.currentHighlight}
                    onChange={commands.onChangeHighlight}
                    onClear={commands.onClearHighlight}
                  />
                </Surface>
              </Popover.Content>
            </Popover.Root>
          ),
        },
        {
          label: 'Text color',
          tag: 'textColor',
          onClick: () => {},
          icon: (
            <Popover.Root>
              <Popover.Trigger asChild>
                <MemoButton active={!!states.currentColor} tooltip='Text color'>
                  <PaletteIcon />
                </MemoButton>
              </Popover.Trigger>
              <Popover.Content side='top' sideOffset={8} asChild>
                <Surface className='p-1'>
                  <MemoColorPicker
                    color={states.currentColor}
                    onChange={commands.onChangeColor}
                    onClear={commands.onClearColor}
                  />
                </Surface>
              </Popover.Content>
            </Popover.Root>
          ),
        },
        {
          label: 'More options',
          tag: 'moreOptions',
          onClick: () => {},
          icon: (
            <Popover.Root>
              <Popover.Trigger asChild>
                <MemoButton
                  tooltip='More options'
                  style={{ transform: 'rotate(90deg)' }}
                >
                  <DotsHorizontal strokeColor='black' />
                </MemoButton>
              </Popover.Trigger>
              <Popover.Content side='top' asChild>
                <Toolbar.Wrapper>
                  <MemoButton
                    tooltip='Subscript'
                    tooltipShortcut={['Mod', '.']}
                    onClick={commands.onSubscript}
                    active={states.isSubscript}
                  >
                    <SubsCriptx />
                  </MemoButton>
                  <MemoButton
                    tooltip='Superscript'
                    tooltipShortcut={['Mod', ',']}
                    onClick={commands.onSuperscript}
                    active={states.isSuperscript}
                  >
                    <SuperScriptx />
                  </MemoButton>
                  <Toolbar.Divider />
                  <MemoButton
                    tooltip='Align left'
                    tooltipShortcut={['Shift', 'Mod', 'L']}
                    onClick={commands.onAlignLeft}
                    active={states.isAlignLeft}
                  >
                    <AlignLeft />
                  </MemoButton>
                  <MemoButton
                    tooltip='Align center'
                    tooltipShortcut={['Shift', 'Mod', 'E']}
                    onClick={commands.onAlignCenter}
                    active={states.isAlignCenter}
                  >
                    <AlignCenter />
                  </MemoButton>
                  <MemoButton
                    tooltip='Align right'
                    tooltipShortcut={['Shift', 'Mod', 'R']}
                    onClick={commands.onAlignRight}
                    active={states.isAlignRight}
                  >
                    <AlignRight />
                  </MemoButton>
                  <MemoButton
                    tooltip='Justify'
                    tooltipShortcut={['Shift', 'Mod', 'J']}
                    onClick={commands.onAlignJustify}
                    active={states.isAlignJustify}
                  >
                    <AlignJustify />
                  </MemoButton>
                </Toolbar.Wrapper>
              </Popover.Content>
            </Popover.Root>
          ),
        },
      ]);
    };
    editor.on('selectionUpdate', handleSelectionUpdate);
    return () => {
      editor.off('selectionUpdate', handleSelectionUpdate);
    };
  }, [editor, blockOptions, commands, states, setNodeSpecificOptions]);

  // if (!selecting) return null;

  return (
    // <Popper
    // open={open && !selecting}
    // anchorEl={anchorEl}
    // placement='top'
    // modifiers={[
    //   {
    //     name: 'offset',
    //     options: {
    //       offset: [0, 8],
    //     },
    //   },
    // ]}
    // sx={{
    //   zIndex: 9999,
    //   '& .MuiPaper-root': {
    //     height: 'unset',
    //   },
    // }}
    // modifiers={[
    //   {
    //     name: 'offset',
    //     options: {
    //       offset: [0, 10],
    //     },
    //   },
    //   {
    //     name: 'flip',
    //     options: {
    //       fallbackPlacements: ['bottom', 'right', 'left'],
    //     },
    //   },
    //   {
    //     name: 'preventOverflow',
    //     options: {
    //       boundary: 'viewport',
    //       padding: 8,
    //     },
    //   },
    //   {
    //     name: 'computeStyles',
    //     options: {
    //       // Use the stored menu position for the popper
    //       gpuAcceleration: false,
    //     },
    //   },
    //   {
    //     name: 'popperOffsets',
    //     enabled: true,
    //     options: {
    //       offset: ({
    //         popper,
    //       }: {
    //         popper: { width: number; height: number };
    //       }) => {
    //         return [
    //           menuPosition.left - popper.width / 2,
    //           menuPosition.top - popper.height,
    //         ];
    //       },
    //     },
    //   },
    // ]}
    // style={{
    //   zIndex: 9999,
    //   position: 'absolute',
    //   transform: 'none !important',
    //   top: `${menuPosition.top - 40}px`,
    //   left: `${menuPosition.left}px`,
    // }}
    // container={appendTo?.current}
    // >
    // <div style={{ display: 'flex', gap: 1, alignItems: 'center' }}>
    //   {/* <AIDropdown
    //       onCompleteSentence={commands.onCompleteSentence}
    //       onEmojify={commands.onEmojify}
    //       onFixSpelling={commands.onFixSpelling}
    //       onMakeLonger={commands.onMakeLonger}
    //       onMakeShorter={commands.onMakeShorter}
    //       onSimplify={commands.onSimplify}
    //       onTldr={commands.onTldr}
    //       onTone={commands.onTone}
    //       // onTranslate={commands.onTranslate}
    //     />
    //     <Toolbar.Divider /> */}
    //   <MemoContentTypePicker options={blockOptions} />
    //   {/* <MemoFontFamilyPicker
    //       onChange={commands.onSetFont}
    //       value={states.currentFont || ''}
    //     /> */}
    //   <MemoFontSizePicker
    //     onChange={commands.onSetFontSize}
    //     value={states.currentSize || ''}
    //   />
    //   <Toolbar.Divider />
    //   <MemoButton
    //     tooltip='Bold'
    //     tooltipShortcut={['Mod', 'B']}
    //     onClick={commands.onBold}
    //     active={states.isBold}
    //   >
    //     <BoldIcon />
    //   </MemoButton>
    //   <MemoButton
    //     tooltip='Italic'
    //     tooltipShortcut={['Mod', 'I']}
    //     onClick={commands.onItalic}
    //     active={states.isItalic}
    //   >
    //     <ItalicIcon />
    //   </MemoButton>
    //   <MemoButton
    //     tooltip='Underline'
    //     tooltipShortcut={['Mod', 'U']}
    //     onClick={commands.onUnderline}
    //     active={states.isUnderline}
    //   >
    //     <UnderlineIcon />
    //   </MemoButton>
    //   <MemoButton
    //     tooltip='Strikethrough'
    //     tooltipShortcut={['Mod', 'Shift', 'S']}
    //     onClick={commands.onStrike}
    //     active={states.isStrike}
    //   >
    //     <StrikethroughIcon />
    //   </MemoButton>
    //   <MemoButton
    //     tooltip='Code'
    //     tooltipShortcut={['Mod', 'E']}
    //     onClick={commands.onCode}
    //     active={states.isCode}
    //   >
    //     <CodeIcon />
    //   </MemoButton>
    //   <MemoButton tooltip='Code block' onClick={commands.onCodeBlock}>
    //     <FileCodeIcon />
    //   </MemoButton>
    //   <EditLinkPopover onSetLink={commands.onLink} />
    //   <Popover.Root>
    //     <Popover.Trigger asChild>
    //       <MemoButton
    //         active={!!states.currentHighlight}
    //         tooltip='Highlight text'
    //       >
    //         <HighlighterIcon />
    //       </MemoButton>
    //     </Popover.Trigger>
    //     <Popover.Content side='top' sideOffset={8} asChild>
    //       <Surface className='p-1'>
    //         <MemoColorPicker
    //           color={states.currentHighlight}
    //           onChange={commands.onChangeHighlight}
    //           onClear={commands.onClearHighlight}
    //         />
    //       </Surface>
    //     </Popover.Content>
    //   </Popover.Root>
    //   <Popover.Root>
    //     <Popover.Trigger asChild>
    //       <MemoButton active={!!states.currentColor} tooltip='Text color'>
    //         <PaletteIcon />
    //       </MemoButton>
    //     </Popover.Trigger>
    //     <Popover.Content side='top' sideOffset={8} asChild>
    //       <Surface className='p-1'>
    //         <MemoColorPicker
    //           color={states.currentColor}
    //           onChange={commands.onChangeColor}
    //           onClear={commands.onClearColor}
    //         />
    //       </Surface>
    //     </Popover.Content>
    //   </Popover.Root>
    //   <Popover.Root>
    //     <Popover.Trigger asChild>
    //       <MemoButton
    //         tooltip='More options'
    //         style={{ transform: 'rotate(90deg)' }}
    //       >
    //         {/* <div
    //             style={{
    //               display: 'flex',
    //               justifyContent: 'center',
    //               transform: 'rotate(90deg)',
    //             }}
    //           > */}
    //         <DotsHorizontal strokeColor='black' />
    //         {/* </div> */}
    //       </MemoButton>
    //     </Popover.Trigger>
    //     <Popover.Content side='top' asChild>
    //       <Toolbar.Wrapper>
    //         <MemoButton
    //           tooltip='Subscript'
    //           tooltipShortcut={['Mod', '.']}
    //           onClick={commands.onSubscript}
    //           active={states.isSubscript}
    //         >
    //           <SubsCriptx />
    //         </MemoButton>
    //         <MemoButton
    //           tooltip='Superscript'
    //           tooltipShortcut={['Mod', ',']}
    //           onClick={commands.onSuperscript}
    //           active={states.isSuperscript}
    //         >
    //           <SuperScriptx />
    //         </MemoButton>
    //         <Toolbar.Divider />
    //         <MemoButton
    //           tooltip='Align left'
    //           tooltipShortcut={['Shift', 'Mod', 'L']}
    //           onClick={commands.onAlignLeft}
    //           active={states.isAlignLeft}
    //         >
    //           <AlignLeft />
    //         </MemoButton>
    //         <MemoButton
    //           tooltip='Align center'
    //           tooltipShortcut={['Shift', 'Mod', 'E']}
    //           onClick={commands.onAlignCenter}
    //           active={states.isAlignCenter}
    //         >
    //           <AlignCenter />
    //         </MemoButton>
    //         <MemoButton
    //           tooltip='Align right'
    //           tooltipShortcut={['Shift', 'Mod', 'R']}
    //           onClick={commands.onAlignRight}
    //           active={states.isAlignRight}
    //         >
    //           <AlignRight />
    //         </MemoButton>
    //         <MemoButton
    //           tooltip='Justify'
    //           tooltipShortcut={['Shift', 'Mod', 'J']}
    //           onClick={commands.onAlignJustify}
    //           active={states.isAlignJustify}
    //         >
    //           <AlignJustify />
    //         </MemoButton>
    //       </Toolbar.Wrapper>
    //     </Popover.Content>
    //   </Popover.Root>
    // </div>
    // </Popper>
    <></>
  );
};
